import { identifyUserInSegment } from '@/lib/segment';
import { readDomainCookie } from '@/lib/domain_cookie';

type IdentifiableAccount = {
  ssoUUID: string;
  email: string;
};

let identifiedSsoUUID: string | null = null;
let inFlightSsoUUID: string | null = null;
let inFlightPromise: Promise<void> | null = null;

async function generateSha1Hash(input: string | null | undefined): Promise<string> {
  if (!input || !crypto?.subtle) return '';
  try {
    const hashBuffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(hashBuffer), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

function layoutAlreadyIdentified(ssoUUID: string): boolean {
  const accountMeta = document.querySelector('meta[name="account"]') as HTMLMetaElement | null;
  return accountMeta?.dataset?.account === ssoUUID;
}

/**
 * Prefer the live gbuuid cookie (same value GrowthBook assignment uses) over the SSR meta
 * snapshot. Meta is missing when GrowthBook creates the cookie client-side after page render,
 * which would otherwise drop the trait GrowthBook needs to join exposures to conversions.
 */
function resolveGbuuid(): string | undefined {
  const fromCookie = readDomainCookie('gbuuid') || undefined;
  if (fromCookie) return fromCookie;

  return document.querySelector('meta[name="gbuuid"]')?.getAttribute('content') || undefined;
}

/**
 * Clears the SPA identify dedupe flag. Call when the authenticated user changes
 * (e.g. logout / registration handoff) so the next account can be identified.
 */
export function resetIdentifiedAccount(): void {
  identifiedSsoUUID = null;
  inFlightSsoUUID = null;
  inFlightPromise = null;
}

/**
 * Identifies the logged-in user in Segment once per SPA lifetime after a confirmed identify.
 * Skips the Vue call when the layout snippet already identified the same user on this page load.
 * Complements the layout snippet so SPA auth handoffs still have an identified user.
 */
export async function identifyAccountInSegmentOnce(account: IdentifiableAccount | null | undefined): Promise<void> {
  if (!account?.ssoUUID) {
    return;
  }

  if (identifiedSsoUUID === account.ssoUUID) {
    return;
  }

  // Layout identify only includes gbuuid when SSR wrote meta[name=gbuuid]. If the cookie
  // exists but meta does not (client-minted after render, or SPA signup on an anonymous shell),
  // fall through and re-identify so GrowthBook can join exposures → user_id metrics.
  if (layoutAlreadyIdentified(account.ssoUUID)) {
    const gbuuid = resolveGbuuid();
    const metaGbuuid = document.querySelector('meta[name="gbuuid"]')?.getAttribute('content') || undefined;
    if (!gbuuid || metaGbuuid) {
      identifiedSsoUUID = account.ssoUUID;
      return;
    }
  }

  if (inFlightSsoUUID === account.ssoUUID && inFlightPromise) {
    await inFlightPromise;
    return;
  }

  inFlightSsoUUID = account.ssoUUID;
  inFlightPromise = (async () => {
    try {
      const { email } = account;
      const emailSha1 = await generateSha1Hash(email);
      const gbuuid = resolveGbuuid();

      const traits: Record<string, string> = { email, emailSha1 };
      if (gbuuid) {
        traits.gbuuid = gbuuid;
      }

      const identified = await identifyUserInSegment(account.ssoUUID, traits);
      if (identified) {
        identifiedSsoUUID = account.ssoUUID;
      }
    } finally {
      if (inFlightSsoUUID === account.ssoUUID) {
        inFlightSsoUUID = null;
        inFlightPromise = null;
      }
    }
  })();

  await inFlightPromise;
}
